import {Component, Inject, Signal, TrackByFunction} from '@angular/core';
import {map, take} from 'rxjs/operators';
import {AbstractControl, FormArray, FormBuilder, FormGroup, ReactiveFormsModule, ValidationErrors, Validators} from '@angular/forms';
import {BannerService} from '../../../banner/banner.service';
import {Apollo} from 'apollo-angular';
import {Airline, Mutation, StartWorkflowVodInput} from '../../../types-workflow';

import {START_WORKFLOW_VOD_DEFAULT} from '../../../queries-workflow';
import {forkJoin, merge} from 'rxjs';
import {Router} from '@angular/router';
import {BasicPoitemVodInfoComponent} from '../basics-poitem-vod-info/basic-poitem-vod-info.component';
import {clean_datetime} from '../../utils';
import {poNumberValidator} from '../../name-limits';
import {Organization} from '../../../types-repository';
import {Po2024Service} from '../../../services/po-2024.service';

import {DoButtonComponent} from '../../do-button/do-button.component';
import {ContextService} from '../../../services/context.service';
import {ProcessService} from '../../../services/process.service';
import {reportInvalid} from '../form-messages';
import {FormFooterComponent} from '../../form-footer/form-footer.component';
import {
  VodDisplayInteractiveFormatSpecificComponent
} from '../vod-display-interactive-format-specific/vod-display-interactive-format-specific.component';

@Component({
  selector: 'app-vod-display-interactive-po',
  templateUrl: './vod-display-interactive-po.component.html',
  styleUrls: ['./vod-display-interactive-po.component.scss'],
  imports: [
    FormFooterComponent,
    ReactiveFormsModule,
    BasicPoitemVodInfoComponent,
    VodDisplayInteractiveFormatSpecificComponent,]
})
export class VodDisplayInteractivePoComponent {
  organization: Signal<Organization>;
  airlines: Signal<Airline[]>;
  poFormGroup: FormGroup;
  /** A submit was refused: from then on the item sub-forms show their invalid fields. */
  submitted = false;

  /**
   * A content supplier's own name, as it appears in the order name.
   *
   * No underscore: "_" separates the three parts of an order name, so one inside the supplier name
   * would shift every later part and the supplier would stop matching its own orders.
   */
  static readonly SUPPLIER_PATTERN = /^[a-z0-9-]+$/;

  /**
   * The title doubles as the DRM content ID, so it is constrained beyond the generic po_item_id
   * pattern shared by every other org's PO form (which stays permissive on purpose).
   */
  private static readonly TITLE_MAX_LENGTH = 212;
  private static readonly TITLE_FORBIDDEN_CHARS = /[:%/_]/;

  private static titleValidator(control: AbstractControl): ValidationErrors | null {
    const title: string = control.value?.po_item_id ?? '';
    const errors: ValidationErrors = {};
    if (title.length > VodDisplayInteractivePoComponent.TITLE_MAX_LENGTH) {
      errors.titleTooLong = true;
    }
    if (VodDisplayInteractivePoComponent.TITLE_FORBIDDEN_CHARS.test(title)) {
      errors.titleForbiddenChars = true;
    }
    return Object.keys(errors).length ? errors : null;
  }

  /**
   * Encryption keys this content off its DRM content ID, so an encrypted item without a title
   * would be indistinguishable from every other encrypted item to DRM.
   */
  private static encryptionRequiresTitleValidator(control: AbstractControl): ValidationErrors | null {
    const encrypt = control.get('format_specific_data')?.value?.encrypt;
    const title: string = control.get('basics')?.value?.po_item_id ?? '';
    return encrypt && !title.trim() ? {encryptionRequiresTitle: true} : null;
  }

  readonly vtkTemplate = 'vod-display-interactive';
  readonly poDestinations = [
    {id: 'displayinteractive', label: 'Display Interactive (S3)'},
    {id: 'vod', label: 'Manual (VOD only)'},
  ];

  poItemTrackBy: TrackByFunction<FormGroup> = (index: number, poitem: FormGroup) => {
    return poitem.get('id').value;
  }

  constructor(
    private apollo: Apollo,
    @Inject('vod_display_interactive') protected poService: Po2024Service,
    private formBuilder: FormBuilder,
    private banner: BannerService,
    contextService: ContextService,
    private processService: ProcessService,
    private router: Router
  ) {
    this.organization = contextService.organization;
    this.airlines = contextService.airlines;

    this.poFormGroup = formBuilder.group({
      airline: [null, Validators.required],
      supplier: [null, [Validators.required, Validators.pattern(VodDisplayInteractivePoComponent.SUPPLIER_PATTERN)]],
      po_number: [clean_datetime(new Date()), [Validators.required, poNumberValidator(() => this.poName)]],
      po_destination: ['displayinteractive', Validators.required],
      auto_publish: [true, Validators.required],
      poItems: formBuilder.array([]),
    });

    // The Project ID's limits are on the whole order name, which also carries airline and supplier.
    merge(
      this.poFormGroup.controls['airline'].valueChanges,
      this.poFormGroup.controls['supplier'].valueChanges
    ).subscribe(() => this.poFormGroup.controls['po_number'].updateValueAndValidity());

    // An operator holds exactly one airline, so there is nothing to choose -- preselect it and the
    // field becomes a confirmation rather than a step. Only a group admin, holding several, picks.
    if (this.airlines()?.length === 1) {
      this.poFormGroup.controls['airline'].setValue(this.airlines()[0].iata_code);
    }

    this.poService.getFormState().pipe(take(1)).subscribe(savedForm => {
      if (savedForm) {
        savedForm.poItems.forEach(poItem =>
          (this.poFormGroup.controls.poItems as FormArray).push(this.buildPoItemGroup(poItem))
        );
        this.poFormGroup.patchValue(savedForm, {emitEvent: false});
      }
    });

    this.poService.poItemToAdd$.subscribe(data => {
      if (data) {
        (this.poFormGroup.controls.poItems as FormArray).push(this.buildPoItemGroup(
          Object.assign(
            {},
            {
              basics: BasicPoitemVodInfoComponent.DEFAULTS,
              format_specific_data: VodDisplayInteractiveFormatSpecificComponent.DEFAULTS
            },
            data
          )
        ));
      }
    });

    this.poService.poItemToRemove$.subscribe(id => {
      if (id) {
        const index = (this.poFormGroup.controls.poItems as FormArray).controls.findIndex(poItem => poItem.get('id').value === id);
        (this.poFormGroup.controls.poItems as FormArray).removeAt(index);
      }
    });
  }

  /**
   * The order name, as "<airline>_<supplier>_<project id>".
   *
   * The parts are load-bearing, not decorative: the workflow derives the airline from the first one,
   * and a content supplier is granted sight of exactly those orders whose second one is its own name.
   * Shown in the form so the operator can see what they are about to create -- a mistyped supplier
   * name produces an order the supplier simply cannot see, with nothing to indicate why.
   */
  get poName(): string {
    const {airline, supplier, po_number} = this.poFormGroup.getRawValue();
    return [airline, supplier, (po_number ?? '').trim()].filter(Boolean).join('_');
  }

  submit_po(button: DoButtonComponent) {
    const organization = this.organization();
    const formValue = this.poFormGroup.getRawValue();

    if (!this.poFormGroup.valid) {
      // Shows every field's error, also those never touched (castlabs-ui gates them on .invalid).
      this.submitted = true;
      this.poFormGroup.markAllAsTouched();
      reportInvalid(button, this.poFormGroup, 'vod-display-interactive-form');
      return;
    }

    const poName = this.poName;
    const workflows = [];
    for (const poItem of formValue.poItems) {
      workflows.push(this.apollo.use('workflow').mutate<Mutation>({
        mutation: START_WORKFLOW_VOD_DEFAULT,
        variables: {
          input_brefix: poItem.basics.input_brefix,
          po_item_id: poItem.basics.po_item_id.trim(),
          po_name: poName,
          po_destination: formValue.po_destination,
          // vod-display-* selects the display-interactive variant in the shared VOD workflow
          vtk_template: this.vtkTemplate,
          auto_publish: formValue.auto_publish,
          format_specific_data: JSON.stringify(poItem.format_specific_data)
        } satisfies StartWorkflowVodInput

      }).pipe(
        map(d => ({
          process: d.data.start_workflow_vod_default,
          po_item_id: poItem.basics.po_item_id.trim()
        }))));
    }

    forkJoin(workflows).subscribe(
      {
        next: wfs => {
          button.done('All encodes started successfully.');
          wfs.forEach(workflow => {
            this.processService.observeProcess(workflow.process, {
              successMessage: `${workflow.po_item_id} encoded.`,
              failureMessage: `Failed to encode ${workflow.po_item_id}.`,
              link: ['/#/o', organization.id, 'workflows',
                formValue.airline,
                poName].join('/')
            });
          });
        },
        error: err => {
          button.done('Workflow submission failed: ' + err, {severity: 'cl-alert-danger'});
        }
      }
    );
  }

  get poItems() {
    return this.poFormGroup.get('poItems') as FormArray<FormGroup>;
  }

  private buildPoItemGroup(poItem: any): FormGroup {
    return this.formBuilder.group({
      ...poItem,
      basics: [poItem.basics, VodDisplayInteractivePoComponent.titleValidator],
    }, {validators: VodDisplayInteractivePoComponent.encryptionRequiresTitleValidator});
  }

  ngOnDestroy(): void {
    this.poService.setFormState(this.poFormGroup.getRawValue());
  }
}
